import { SLASH_HINT_UPDATE_TAG } from '#pplx/frontend/components/form/ResizableInput/constants.js';
import { $createSlashHintNode, $isSlashHintNode, } from '#pplx/frontend/composer-sdk/lexical/nodes/SlashHintNodeSupport.js';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot, $getSelection, $isElementNode, $isRangeSelection, $isTextNode } from 'lexical';
import { useEffect, useRef } from 'react';
function removeSlashHintNodes(root) {
    let removed = false;
    for (const child of root.getChildren()) {
        if ($isSlashHintNode(child)) {
            child.remove();
            removed = true;
            continue;
        }
        if ($isElementNode(child)) {
            for (const grandchild of child.getChildren()) {
                if ($isSlashHintNode(grandchild)) {
                    grandchild.remove();
                    removed = true;
                }
            }
        }
    }
    return removed;
}
function hasSlashHintNodes(root) {
    for (const child of root.getChildren()) {
        if ($isSlashHintNode(child))
            return true;
        if ($isElementNode(child) && child.getChildren().some($isSlashHintNode)) {
            return true;
        }
    }
    return false;
}
function getActiveSlashQuery(textBeforeCursor) {
    const triggerMatch = Array.from(textBeforeCursor.matchAll(/(^|\s)\/([^\s/]*)$/g)).pop();
    const triggerIndex = triggerMatch?.index;
    if (!triggerMatch || triggerIndex === undefined)
        return null;
    const leadingText = triggerMatch[1] ?? '';
    const query = triggerMatch[2] ?? '';
    return {
        query,
        slashOffset: triggerIndex + leadingText.length,
    };
}
export function SlashCommandHintPlugin({ hintText, onSearchQueryChange, }) {
    const [editor] = useLexicalComposerContext();
    const previousQueryRef = useRef(undefined);
    useEffect(() => {
        if (!onSearchQueryChange)
            return;
        return () => {
            if (previousQueryRef.current !== undefined) {
                previousQueryRef.current = undefined;
                onSearchQueryChange(undefined);
            }
        };
    }, [onSearchQueryChange]);
    useEffect(() => {
        return editor.registerUpdateListener(({ editorState, tags }) => {
            if (tags.has(SLASH_HINT_UPDATE_TAG))
                return;
            let activeQuery;
            let shouldShowHint = false;
            let hasExistingHint = false;
            editorState.read(() => {
                hasExistingHint = hasSlashHintNodes($getRoot());
                const selection = $getSelection();
                if (!$isRangeSelection(selection) || !selection.isCollapsed())
                    return;
                const anchorNode = selection.anchor.getNode();
                if (!$isTextNode(anchorNode))
                    return;
                const activeSlashQuery = getActiveSlashQuery(anchorNode.getTextContent().slice(0, selection.anchor.offset));
                if (!activeSlashQuery)
                    return;
                activeQuery = activeSlashQuery.query;
                shouldShowHint = Boolean(hintText) && activeSlashQuery.query.length === 0;
            });
            if (previousQueryRef.current !== activeQuery) {
                previousQueryRef.current = activeQuery;
                onSearchQueryChange?.(activeQuery);
            }
            if (!shouldShowHint && !hasExistingHint)
                return;
            editor.update(() => {
                const root = $getRoot();
                const selection = $getSelection();
                if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
                    removeSlashHintNodes(root);
                    return;
                }
                const anchorNode = selection.anchor.getNode();
                if (!$isTextNode(anchorNode)) {
                    removeSlashHintNodes(root);
                    return;
                }
                const activeSlashQuery = getActiveSlashQuery(anchorNode.getTextContent().slice(0, selection.anchor.offset));
                if (!activeSlashQuery || !shouldShowHint || !hintText) {
                    removeSlashHintNodes(root);
                    return;
                }
                const insertionOffset = activeSlashQuery.slashOffset + 1;
                let nodeBeforeHint = anchorNode;
                if (insertionOffset < anchorNode.getTextContentSize()) {
                    const splitNode = anchorNode.splitText(insertionOffset)[0];
                    if (!splitNode) {
                        removeSlashHintNodes(root);
                        return;
                    }
                    nodeBeforeHint = splitNode;
                }
                const nextSibling = nodeBeforeHint.getNextSibling();
                if ($isSlashHintNode(nextSibling) && nextSibling.__hintText === hintText) {
                    nodeBeforeHint.selectEnd();
                    return;
                }
                removeSlashHintNodes(root);
                nodeBeforeHint.insertAfter($createSlashHintNode(hintText));
                nodeBeforeHint.selectEnd();
            }, { tag: SLASH_HINT_UPDATE_TAG });
        });
    }, [editor, hintText, onSearchQueryChange]);
    return null;
}
//# sourceMappingURL=SlashCommandHintPlugin.js.map